import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {Button, Checkbox, ComboBox, Detail, EMPHASIS, FlexRow, Text} from '@forecast-it/design-system';
import {FEATURE_FLAG_CARD_BORDER, FEATURE_FLAG_NEUTRAL_FILL} from '../featureFlagSurface';
import {RolloutPersonOption} from './featureFlagRolloutDirectory';

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const LockedField = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	height: 40px;
	padding: 0 12px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
`;

const AllUsersRow = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	margin-top: 24px;
`;

const Note = styled.div`
	margin-top: 14px;
	padding: 12px 16px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-radius: 4px;
`;

const PickerRow = styled.div`
	display: flex;
	align-items: flex-end;
	gap: 10px;
`;

const PickerBlock = styled.div`
	flex: 1;
	min-width: 0;
`;

const UserList = styled.div`
	margin-top: 16px;
	border: 1px solid ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
	overflow: hidden;
`;

const UserListHead = styled.div`
	padding: 9px 14px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const UserRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	min-height: 40px;
	padding: 6px 8px 6px 14px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	&:last-child {
		border-bottom: none;
	}
`;

const EmptyUsers = styled.div`
	margin-top: 16px;
	padding: 20px;
	text-align: center;
	border: 1px dashed ${FEATURE_FLAG_CARD_BORDER};
	border-radius: 4px;
`;

const UserPickerBlock = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-top: 14px;
`;

export interface RolloutSpecificTabProps {
	/** Every company, with eligibility as a separate flag rather than baked into the name. Unused in edit mode. */
	companyOptions: Array<{id: string; name: string; active: boolean}>;
	selectedCompanyId: string | null;
	/** Set when editing an existing company: the field renders locked and no picker is shown. */
	lockedCompanyName: string | null;
	onSelectCompany: (companyId: string | null) => void;
	allUsers: boolean;
	onAllUsersChange: (allUsers: boolean) => void;
	/** People at the selected company who are not already chosen. */
	personOptions: RolloutPersonOption[];
	/** The full set the company will end up with, not just newly added people. */
	selectedPersons: RolloutPersonOption[];
	onAddPerson: (personId: string) => void;
	onRemovePerson: (personId: string) => void;
}

export function RolloutSpecificTab({
	companyOptions,
	selectedCompanyId,
	lockedCompanyName,
	onSelectCompany,
	allUsers,
	onAllUsersChange,
	personOptions,
	selectedPersons,
	onAddPerson,
	onRemovePerson,
}: RolloutSpecificTabProps): JSX.Element {
	// Which person the picker is showing, held here because it is scratch state for the Add control
	// rather than part of what the modal will save.
	const [pendingPersonId, setPendingPersonId] = useState<string | null>(null);
	// Bumped after an add, and whenever the effect below invalidates a pending pick, so the picker
	// remounts and clears.
	const [personPickerResetToken, setPersonPickerResetToken] = useState(0);

	// A pending pick belongs to the company that was selected when it was made, and only survives as
	// long as the picker that made it stays on screen. Both of the things that invalidate it reset it:
	//
	//   - Switching companies. Without this, picking a person, then switching companies before pressing
	//     Add, would grant that person under the NEW company on Add — invisible on screen, since
	//     neither the new company's directory nor its row knows them, but sent to the server regardless.
	//   - Toggling "Select all users", which unmounts the picker block entirely. Unticking it again
	//     mounts an empty field, so a pending pick kept across the toggle would be committed by Add
	//     from a field that is visibly showing nobody.
	useEffect(() => {
		setPendingPersonId(null);
		setPersonPickerResetToken(token => token + 1);
	}, [allUsers, selectedCompanyId]);

	const handleAddPerson = (): void => {
		if (pendingPersonId == null) {
			return;
		}
		onAddPerson(pendingPersonId);
		setPendingPersonId(null);
		setPersonPickerResetToken(token => token + 1);
	};

	// Filter out people already selected from the available options
	const selectedPersonIds = new Set(selectedPersons.map(p => p.id));
	const availablePersonOptions = personOptions.filter(p => !selectedPersonIds.has(p.id));

	const userCountLabel = selectedPersons.length === 1 ? '1 user with access' : `${selectedPersons.length} users with access`;

	return (
		<div>
			<Section>
				<MicroLabel>
					<Detail size="2">SELECT CUSTOMER</Detail>
				</MicroLabel>
				{lockedCompanyName != null ? (
					<LockedField>
						<Text>{lockedCompanyName}</Text>
						<Detail color="medium">Locked</Detail>
					</LockedField>
				) : (
					<>
						{/* defaultItems rather than items, so typing filters the list — a controlled list is
						    never run through the built-in filter. `value`, by contrast, IS controlled: it
						    makes the field's selection follow `selectedCompanyId` instead of react-aria's
						    own copy, so clearing the input text cannot leave the field blank while a
						    company is still selected and about to be saved. The text resyncs to the
						    selected company's name on blur. */}
						<ComboBox
							label={'Select customer'}
							onChange={(companyId: string | null) => onSelectCompany(companyId)}
							placeholder={'Search companies'}
							defaultItems={companyOptions}
							value={selectedCompanyId}
							width={ComboBox.WIDTH.FLEX}
						>
							{(item: {id: string; name: string; active: boolean}) => (
								<ComboBox.Item textValue={item.name}>
									<FlexRow gap="xs">
										<Text>{item.name}</Text>
										{item.active ? <Detail color="medium">(active)</Detail> : null}
									</FlexRow>
								</ComboBox.Item>
							)}
						</ComboBox>
						<Detail color="medium">
							Active means the company is not on a trial or free-forever plan and is not blocked.
						</Detail>
					</>
				)}
			</Section>

			{selectedCompanyId == null ? null : (
				<div>
					<AllUsersRow>
						<Checkbox
							checked={allUsers}
							labelText={'Select all users'}
							onChange={() => onAllUsersChange(!allUsers)}
						/>
					</AllUsersRow>

					{allUsers ? (
						<Note>
							<Text>All users in this company will get access.</Text>
						</Note>
					) : (
						<div>
							<UserPickerBlock>
								<MicroLabel>
									<Detail size="2">SELECT USERS</Detail>
								</MicroLabel>
								<PickerRow>
									<PickerBlock>
										<ComboBox
											key={personPickerResetToken}
											label={'Select users'}
											onChange={(personId: string | null) => setPendingPersonId(personId)}
											placeholder={'Search users'}
											defaultItems={availablePersonOptions}
											width={ComboBox.WIDTH.FLEX}
										>
											{(item: RolloutPersonOption) => (
												<ComboBox.Item textValue={item.name}>
													<Text>{item.name}</Text>
												</ComboBox.Item>
											)}
										</ComboBox>
									</PickerBlock>
									<Button emphasis={EMPHASIS.MEDIUM} onPress={handleAddPerson}>
										Add
									</Button>
								</PickerRow>
							</UserPickerBlock>

							{selectedPersons.length === 0 ? (
								<EmptyUsers>
									<Detail color="medium">No individual users selected yet.</Detail>
								</EmptyUsers>
							) : (
								<UserList>
									<UserListHead>
										<Detail size="2">{userCountLabel}</Detail>
									</UserListHead>
									{selectedPersons.map(person => (
										<UserRow data-cy="rollout-selected-user" key={person.id}>
											<Text>{person.name}</Text>
											{/* Identified for tests by the person it removes. The design system's
											    Button derives its own name from an icon-only button's icon and
											    discards anything a caller passes, so there is no per-row name to
											    address it by. */}
											<Button
												data-testid={`rollout-remove-${person.id}`}
												emphasis={EMPHASIS.LOW}
												icon="trash"
												onPress={() => onRemovePerson(person.id)}
											/>
										</UserRow>
									))}
								</UserList>
							)}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
